import { useState, useEffect, useCallback, RefObject } from 'react'

type BackgroundType = 'light' | 'dark'

// Tailwind classes that indicate dark backgrounds
const DARK_CLASS_PATTERNS = [
  /\bfrom-gray-[89]00\b/,
  /\bvia-gray-[89]00\b/,
  /\bto-gray-[89]00\b/,
  /\bbg-gray-[89]00\b/,
  /\bbg-black\b/,
  /\bfrom-black\b/,
  /\bbg-slate-[89]00\b/,
  /\bfrom-slate-[89]00\b/,
]

function hasDarkClasses(element: Element | null): boolean {
  if (!element || element === document.documentElement) return false

  const className = element.className
  if (typeof className === 'string') {
    if (DARK_CLASS_PATTERNS.some(pattern => pattern.test(className))) {
      return true
    }
  }

  // Check parent elements (up to 5 levels)
  let parent = element.parentElement
  let depth = 0
  while (parent && depth < 5) {
    const parentClassName = parent.className
    if (typeof parentClassName === 'string') {
      if (DARK_CLASS_PATTERNS.some(pattern => pattern.test(parentClassName))) {
        return true
      }
    }
    parent = parent.parentElement
    depth++
  }

  return false
}

function isColorDark(color: string): boolean {
  // Parse RGB values
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
  if (!match) return false

  const [, r, g, b] = match.map(Number)

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance < 0.35
}

function checkElementBackground(element: Element | null, depth = 0): boolean {
  if (!element || element === document.documentElement || depth > 10) return false

  // First check CSS classes (for Tailwind gradients)
  if (hasDarkClasses(element)) {
    return true
  }

  const style = window.getComputedStyle(element)
  const bgColor = style.backgroundColor

  // Check solid background color
  if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
    if (isColorDark(bgColor)) {
      return true
    }
  }

  // Check background image (for CSS gradients)
  const bgImage = style.backgroundImage
  if (bgImage && bgImage !== 'none') {
    // Check for dark colors in gradient
    if (bgImage.includes('rgb(17, 24, 39)') ||
        bgImage.includes('rgb(31, 41, 55)') ||
        bgImage.includes('rgb(0, 0, 0)')) {
      return true
    }
  }

  // If transparent, check parent
  if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') {
    return checkElementBackground(element.parentElement, depth + 1)
  }

  return false
}

interface UseBackgroundContrastOptions {
  offset?: { x?: number; y?: number }
  containerRef?: RefObject<HTMLElement | null>
}

export function useBackgroundContrast(
  elementRef: RefObject<HTMLElement | null>,
  options: UseBackgroundContrastOptions = {}
): BackgroundType {
  const { offset = {}, containerRef } = options
  const [background, setBackground] = useState<BackgroundType>('light')

  const checkBackground = useCallback(() => {
    if (!elementRef.current) return

    const rect = elementRef.current.getBoundingClientRect()
    const x = rect.left + rect.width / 2 + (offset.x || 0)
    const y = rect.top + rect.height / 2 + (offset.y || 0)

    // Hide the element and optionally its container to find what's behind
    const originalVisibility = elementRef.current.style.visibility
    elementRef.current.style.visibility = 'hidden'

    let originalContainerVisibility: string | undefined
    if (containerRef?.current) {
      originalContainerVisibility = containerRef.current.style.visibility
      containerRef.current.style.visibility = 'hidden'
    }

    const elementBehind = document.elementFromPoint(x, y)

    // Restore visibility
    elementRef.current.style.visibility = originalVisibility
    if (containerRef?.current && originalContainerVisibility !== undefined) {
      containerRef.current.style.visibility = originalContainerVisibility
    }

    if (elementBehind) {
      const isDark = checkElementBackground(elementBehind)
      setBackground(isDark ? 'dark' : 'light')
    }
  }, [elementRef, containerRef, offset.x, offset.y])

  useEffect(() => {
    // Check after a brief delay to ensure DOM is ready
    const initialTimeout = setTimeout(checkBackground, 100)

    // Check on scroll and resize
    const handleScroll = () => {
      requestAnimationFrame(checkBackground)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll, { passive: true })

    return () => {
      clearTimeout(initialTimeout)
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [checkBackground])

  return background
}
